type ResponseSummary = {
  status?: number
  statusText?: string
  type?: string
  url?: string
}

type SentryNetworkErrorContext = {
  code?: number | string
  response?: ResponseSummary
  statusCode?: number
  value?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getString = (value: unknown) =>
  typeof value === 'string' ? value : undefined

const getNumber = (value: unknown) =>
  typeof value === 'number' ? value : undefined

const getCode = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined

const getResponseSummary = (value: unknown): ResponseSummary | undefined => {
  if (!isRecord(value)) {
    return undefined
  }

  const response = value as Record<string, unknown>
  const responseSummary = {
    status: getNumber(response.status),
    statusText: getString(response.statusText),
    type: getString(response.type),
    url: getString(response.url),
  }

  return responseSummary.status !== undefined ||
    responseSummary.statusText !== undefined ||
    responseSummary.type !== undefined ||
    responseSummary.url !== undefined
    ? responseSummary
    : undefined
}

// Keep only bounded Apollo/Fetch metadata; captureException records Error details,
// and response result/body data is intentionally excluded.
export const getSentryNetworkErrorContext = (
  networkError: unknown
): SentryNetworkErrorContext | undefined => {
  if (networkError === undefined || networkError === null) {
    return undefined
  }

  if (!isRecord(networkError)) {
    return { value: networkError }
  }

  const error = networkError as Error & Record<string, unknown>
  const context = {
    code: getCode(error.code),
    response: getResponseSummary(error.response),
    statusCode: getNumber(error.statusCode),
  }

  return context.code !== undefined ||
    context.response !== undefined ||
    context.statusCode !== undefined
    ? context
    : undefined
}

// Group failures by GraphQL operation even when their exception types or stacks differ.
export const getGraphQLErrorFingerprint = (operationName: string) => [
  'gql-error',
  operationName,
  `GQL Operation failed: ${operationName}`,
]
